// Pure transcript and turn helpers for the Ducky chat. No Supabase import, so
// the hook's state logic can be unit-tested under node:test.

import type { Database } from "../../lib/database.types";

export type AgentConversation =
  Database["public"]["Tables"]["agent_conversations"]["Row"];
export type AgentMessage =
  Database["public"]["Tables"]["agent_messages"]["Row"];
export type AgentTurn = Database["public"]["Tables"]["agent_turns"]["Row"];

// The agent runtime (HAZ-49) writes assistant and tool messages; the
// browser only writes user messages. tool_calls is structured — an array
// of applied-change entries the UI renders as summaries — not raw provider
// tool-call payloads. `kind` is set by the runtime on every entry; only
// `applied` entries changed the site.
export type AppliedChange = {
  tool: string;
  summary: string;
  kind?: "applied" | "skipped" | "failed";
};

export const appliedChangesOf = (message: AgentMessage): AppliedChange[] => {
  if (message.role !== "tool" || !Array.isArray(message.tool_calls)) return [];
  return message.tool_calls.filter(
    (entry): entry is AppliedChange =>
      typeof entry === "object" &&
      entry !== null &&
      typeof (entry as { tool?: unknown }).tool === "string" &&
      typeof (entry as { summary?: unknown }).summary === "string",
  );
};

export type AgentChatMessage = {
  id: string;
  role: AgentMessage["role"];
  content: string;
  changes: AppliedChange[];
  createdAt: string;
};

export const toChatMessage = (message: AgentMessage): AgentChatMessage => ({
  id: message.id,
  role: message.role,
  content: message.content,
  changes: appliedChangesOf(message),
  createdAt: message.created_at,
});

export const PENDING_USER_PREFIX = "pending-user-";

// Add a row that arrived over Realtime. Rows already present (by id) are
// replaced in place, never duplicated: the same row can arrive from the
// subscription, a transcript reload and the save that created it.
export const mergeMessage = (
  current: AgentChatMessage[],
  incoming: AgentChatMessage,
): AgentChatMessage[] => {
  const index = current.findIndex((message) => message.id === incoming.id);
  if (index === -1) return [...current, incoming];
  return current.map((message, i) => (i === index ? incoming : message));
};

// A transcript reload is the source of truth (Realtime delivery isn't
// guaranteed), but a message still being saved has no server row yet — keep
// its optimistic row after the server's rows.
export const reconcileMessages = (
  current: AgentChatMessage[],
  server: AgentChatMessage[],
): AgentChatMessage[] => {
  const known = new Set(server.map((message) => message.id));
  const pending = current.filter(
    (message) =>
      message.id.startsWith(PENDING_USER_PREFIX) && !known.has(message.id),
  );
  return [...server, ...pending];
};

// The browser saved the user's row: swap it in for the optimistic one. If
// the Realtime INSERT for that row won the race, the saved row is already
// in the list and the optimistic one is simply dropped.
export const confirmPendingMessage = (
  current: AgentChatMessage[],
  pendingId: string,
  saved: AgentChatMessage,
): AgentChatMessage[] => {
  if (current.some((message) => message.id === saved.id)) {
    return current.filter((message) => message.id !== pendingId);
  }
  return current.map((message) => message.id === pendingId ? saved : message);
};

// What a finished turn changed, from its own tool rows. `kind` guards against
// counting failed or skipped calls as applied work.
export const appliedChangesForTurn = (
  rows: AgentMessage[],
  turnId: string,
): AppliedChange[] =>
  rows
    .filter((row) => row.turn_id === turnId)
    .flatMap(appliedChangesOf)
    .filter((change) => change.kind === "applied");

export const isTurnRunning = (turn: AgentTurn | null): boolean =>
  turn?.status === "running";

export const isTurnTerminal = (turn: AgentTurn | null): turn is AgentTurn =>
  turn !== null && turn.status !== "running";

// Which turn the chat follows. The same turn always updates in place; a
// different turn wins only if it is not older (a conversation runs one turn
// at a time, so the newest is the one that matters).
export const nextTurn = (
  current: AgentTurn | null,
  incoming: AgentTurn,
): AgentTurn => {
  if (!current || current.id === incoming.id) return incoming;
  return Date.parse(incoming.created_at) >= Date.parse(current.created_at)
    ? incoming
    : current;
};

// A running turn the browser knows only by id, from the 202 response, until
// the real row arrives over Realtime or a fetch.
export const runningTurnStub = (
  turnId: string,
  conversationId: string,
  userId: string,
  now = new Date(),
): AgentTurn => ({
  id: turnId,
  conversation_id: conversationId,
  user_id: userId,
  status: "running",
  stopped_reason: null,
  cancel_requested: false,
  heartbeat_at: now.toISOString(),
  created_at: now.toISOString(),
  finished_at: null,
});

export type TurnPhase =
  | "idle"
  | "working"
  | "stopping"
  | "done"
  | "cancelled"
  | "failed";

// One value the panel can render from: whether Ducky is busy, and how the
// last turn ended.
export const turnPhase = (
  turn: AgentTurn | null,
  starting: boolean,
  stopRequestedFor: string | null,
): TurnPhase => {
  if (turn && isTurnRunning(turn)) {
    return turn.cancel_requested || stopRequestedFor === turn.id
      ? "stopping"
      : "working";
  }
  if (starting) return "working";
  return turn ? turn.status as TurnPhase : "idle";
};
